
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type Favorite = {
  id: string;
  user_id: string;
  service_id: string;
  is_favorite: boolean;
  created_at: string;
};

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loadingFavorites, setLoadingFavorites] = useState<string[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return;

        const { data, error } = await supabase
          .from('favorites')
          .select('*')
          .eq('user_id', user.id);

        if (error) throw error;
        setFavorites(data || []);
      } catch (error) {
        console.error("Error fetching favorites:", error);
      }
    };

    fetchFavorites();
    
    // Subscribe to changes
    const channel = supabase
      .channel('favorites-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'favorites' }, () => {
        fetchFavorites();
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const isFavorite = (serviceId: string): boolean => {
    return favorites.some(fav => fav.service_id === serviceId && fav.is_favorite);
  };

  const isTogglingFavorite = (serviceId: string): boolean => {
    return loadingFavorites.includes(serviceId);
  };

  const toggleFavorite = async (serviceId: string): Promise<boolean> => {
    try {
      setLoadingFavorites(prev => [...prev, serviceId]);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Connexion requise",
          description: "Veuillez vous connecter pour gérer vos favoris.",
          variant: "destructive"
        });
        return false;
      }

      const currentFav = favorites.find(fav => fav.service_id === serviceId);
      const newStatus = !isFavorite(serviceId);
      
      if (currentFav) {
        // Update existing favorite
        const { error } = await supabase
          .from('favorites')
          .update({ is_favorite: newStatus })
          .eq('id', currentFav.id);
          
        if (error) throw error;
      } else {
        // Create new favorite
        const { error } = await supabase
          .from('favorites')
          .insert({
            user_id: user.id,
            service_id: serviceId,
            is_favorite: true
          });
          
        if (error) throw error;
      }
      
      toast({
        title: newStatus ? "Ajouté aux favoris" : "Retiré des favoris",
        description: newStatus ? "Élément ajouté à vos favoris" : "Élément retiré de vos favoris"
      });
      
      return true;
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour les favoris.",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoadingFavorites(prev => prev.filter(id => id !== serviceId));
    }
  };
  
  return { favorites, isFavorite, toggleFavorite, isTogglingFavorite };
};
